import { useEffect, useState } from 'react';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from '../../atoms/Button';
import { AddBrandService } from '../../../services/brands';
import { ImageUpload } from '../../atoms/UploadImage';

interface FormData {
  brandname: string;
  brandLogoUrl: string;
}

interface BrandAddEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}
export const AddEditBrandModal = ({
  open,
  onClose,
  onSaved,
}: BrandAddEditProps) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [formData, setFormData] = useState<FormData>({
    brandname: '',
    brandLogoUrl: '',
  });
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    handleReset();
  }, [open]);
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    try {
      if (!formData.brandname) {
        showSnackbar('Fill brand name!', 'error');
        return;
      }
      const status = await AddBrandService({
        name: formData.brandname,
        brandLogoUrl: formData.brandLogoUrl,
      });

      if (status === 200) {
        await showSnackbar('Brand created successfully!', 'success');

        setTimeout(() => {
          onClose();
          handleReset();
          if (onSaved) {
            onSaved(); // Trigger the onSaved callback with formData
          }
        }, 1500);
      } else {
        showSnackbar('Error: Failed to create brand', 'error');
      }
    } catch (error: any) {
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };

  const handleReset = () => {
    setFormData({
      brandname: '',
      brandLogoUrl: '',
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
        PaperProps={{
          sx: {
            borderRadius: 10,
            overflow: 'hidden', // Ensures the background doesn't leak
          },
        }}
      >
        <Box
          sx={{
            border: '1px solid #7B1FA2', // Add border here
            borderRadius: 10,
            overflow: 'hidden', // Prevent border from affecting layout
          }}
        >
          {SnackbarComponent}
          <DialogTitle
            sx={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#181818',
              padding: '16px',
            }}
          >
            {'Add Brand'}
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography gutterBottom>Brand Name</Typography>
                <TextField
                  name="brandname"
                  value={formData.brandname}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom>Brand Logo</Typography>
                <ImageUpload
                  imageUrl={formData.brandLogoUrl}
                  onChange={(url) =>
                    setFormData((prev) => ({ ...prev, brandLogoUrl: url }))
                  }
                  onRemove={() =>
                    setFormData((prev) => ({ ...prev, brandLogoUrl: '' }))
                  }
                  setLoader={setIsUploading}
                  uploadLabel="Upload Brand Logo"
                  fileId="brand-logo-upload"
                  type="icon"
                  disabled={false}
                  folderType={11}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
              <SecondaryButton title="Cancel" onClick={onClose} />
              <PrimaryButton
                title="Save"
                disabled={isUploading}
                onClick={(e) => handleSubmit(e)}
              />
            </DialogActions>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};
